import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Sparkles, Loader2, Send, RotateCcw, DollarSign, FileSignature,
  ListChecks, Lightbulb, ArrowRight, MessageCircleQuestion, Bot, User as UserIcon,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { facilityAIAnalysis, facilityAIChat } from "@/lib/api";

/**
 * FacilityAIPanel — Phase 1 AI Assistant for a single facility.
 *
 * The structured analysis is cached on `facility.ai_analysis` (server-side),
 * so re-opening a facility shows the last brief instantly. "Regenerate" forces
 * a fresh Claude call. The conversational chat below is scoped to this facility
 * via `session_id = facility-chat-{id}` so the model retains context within a
 * single CRM session without leaking across records.
 *
 * Every AI action requires confirmation (user-initiated click), and is logged
 * to the facility's audit/activity log by the backend.
 */
export default function FacilityAIPanel({ facility, onFacilityUpdate }) {
  const cached = facility?.ai_analysis;
  const [analysis, setAnalysis] = useState(cached || null);
  const [analyzing, setAnalyzing] = useState(false);
  const [messages, setMessages] = useState(() => seedConversation(facility));
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    setAnalysis(facility?.ai_analysis || null);
    setMessages(seedConversation(facility));
  }, [facility?.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const runAnalysis = async () => {
    if (!facility?.id) return;
    try {
      setAnalyzing(true);
      const a = await facilityAIAnalysis(facility.id);
      setAnalysis(a);
      onFacilityUpdate?.({ ...facility, ai_analysis: a, last_activity_at: a.generated_at });
      toast.success("AI brief refreshed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate AI brief.");
    } finally {
      setAnalyzing(false);
    }
  };

  const sendMessage = async (text) => {
    const message = (text ?? draft).trim();
    if (!message || sending) return;
    setMessages((m) => [...m, { role: "user", text: message }]);
    setDraft("");
    try {
      setSending(true);
      const res = await facilityAIChat(facility.id, { message, session_id: `facility-chat-${facility.id}` });
      setMessages((m) => [...m, { role: "assistant", text: res.reply || "(no response)" }]);
      onFacilityUpdate?.({ ...facility, last_activity_at: new Date().toISOString() });
    } catch (e) {
      const err = e?.response?.data?.detail || "Chat failed.";
      setMessages((m) => [...m, { role: "assistant", text: `⚠️ ${err}`, error: true }]);
      toast.error(err);
    } finally {
      setSending(false);
    }
  };

  const briefRange = useMemo(() => {
    if (!analysis) return null;
    const lo = Math.round(analysis.estimated_monthly_revenue_low || 0);
    const hi = Math.round(analysis.estimated_monthly_revenue_high || 0);
    if (!lo && !hi) return null;
    if (lo === hi) return `$${lo.toLocaleString()}`;
    return `$${lo.toLocaleString()} – $${hi.toLocaleString()}`;
  }, [analysis]);

  return (
    <div className="space-y-5" data-testid="facility-ai-panel">
      {/* AI Brief */}
      <section
        className="rounded-2xl border p-5 sm:p-6"
        style={{
          borderColor: "rgba(212,175,55,0.4)",
          background: "linear-gradient(180deg, rgba(11,37,69,0.04) 0%, rgba(212,175,55,0.05) 100%)",
        }}
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="grid place-items-center h-10 w-10 rounded-xl shrink-0"
              style={{ background: "#0B2545", color: "#D4AF37" }}>
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: "#7A5E12" }}>
                AI Opportunity Brief
              </div>
              <h3 className="font-display text-xl font-semibold mt-0.5" style={{ color: "#0B2545" }}>
                {facility?.facility_name}
              </h3>
              {analysis?.generated_at ? (
                <div className="text-[11px] mt-0.5" style={{ color: "#94A3B8" }}>
                  Generated {new Date(analysis.generated_at).toLocaleString()}
                </div>
              ) : null}
            </div>
          </div>
          <Button
            size="sm"
            onClick={runAnalysis}
            disabled={analyzing}
            className="h-9"
            style={{ background: "#0B2545", color: "#FFF" }}
            data-testid="facility-ai-run-analysis"
          >
            {analyzing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : analysis
              ? <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
            {analysis ? "Regenerate brief" : "Generate AI brief"}
          </Button>
        </div>

        {analysis ? (
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <BriefCard
              icon={DollarSign}
              label="Estimated Monthly Revenue"
              value={briefRange || "—"}
              accent="#065F46"
              testId="ai-brief-revenue"
            />
            <BriefCard
              icon={FileSignature}
              label="Recommended Pricing Model"
              value={analysis.recommended_pricing_model || "—"}
              accent="#1D4ED8"
              testId="ai-brief-pricing"
            />
            <BriefCard
              icon={ListChecks}
              label="Suggested Contract Length"
              value={analysis.suggested_contract_months ? `${analysis.suggested_contract_months} months` : "—"}
              accent="#6B21A8"
              testId="ai-brief-contract"
            />
            <BriefCard
              icon={Lightbulb}
              label="Recommended Services"
              value={(analysis.recommended_services && analysis.recommended_services.length)
                ? analysis.recommended_services.slice(0, 3).join(" · ")
                : "—"}
              accent="#9A3412"
              testId="ai-brief-services"
              wrap
            />
          </div>
        ) : (
          <div className="mt-5 rounded-xl border border-dashed p-5 text-center" style={{ borderColor: "rgba(11,37,69,0.18)" }}>
            <Sparkles className="h-5 w-5 mx-auto mb-2" style={{ color: "#D4AF37" }} />
            <div className="font-display text-base font-semibold" style={{ color: "#0B2545" }}>
              No AI brief generated yet
            </div>
            <p className="mt-1 text-xs max-w-md mx-auto" style={{ color: "#475569" }}>
              Generate an opportunity brief to see estimated revenue, recommended pricing, suggested contract length, and tactical insights.
            </p>
          </div>
        )}

        {/* Insights */}
        {analysis?.insights?.length ? (
          <div className="mt-5">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-2" style={{ color: "#475569" }}>
              AI Insights
            </div>
            <ul className="space-y-2" data-testid="ai-insights-list">
              {analysis.insights.map((line, i) => (
                <li key={i} className="flex items-start gap-2.5 rounded-lg border bg-white px-3 py-2.5"
                  style={{ borderColor: "#E2E8F0" }}>
                  <Lightbulb className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: "#D4AF37" }} />
                  <span className="text-sm leading-snug" style={{ color: "#0B2545" }}>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {analysis?.follow_up_suggestion ? (
          <div className="mt-4 rounded-lg border px-3 py-2.5 flex items-start gap-2"
            style={{ borderColor: "rgba(11,37,69,0.18)", background: "#F8FAFC" }}>
            <ArrowRight className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: "#0B2545" }} />
            <div className="text-sm" style={{ color: "#0B2545" }}>
              <span className="font-semibold">Suggested next step: </span>
              <span>{analysis.follow_up_suggestion}</span>
            </div>
          </div>
        ) : null}
      </section>

      {/* Ask AI Assistant — conversational chat */}
      <section
        className="rounded-2xl border bg-white overflow-hidden"
        style={{ borderColor: "#E2E8F0" }}
        data-testid="facility-ai-chat-section"
      >
        <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: "#E2E8F0", background: "#F8FAFC" }}>
          <MessageCircleQuestion className="h-4 w-4" style={{ color: "#0B2545" }} />
          <span className="text-[12px] font-semibold" style={{ color: "#0B2545" }}>Ask AI Assistant</span>
          <span className="ml-auto text-[10px]" style={{ color: "#94A3B8" }}>Claude Sonnet</span>
        </div>

        <div ref={scrollRef} className="max-h-[280px] overflow-y-auto px-4 py-3 space-y-3" data-testid="facility-ai-messages">
          {messages.map((m, i) => (
            <MessageBubble key={i} role={m.role} text={m.text} error={m.error} />
          ))}
          {sending ? <MessageBubble role="assistant" loading /> : null}
        </div>

        {/* Suggested prompts */}
        <div className="px-4 pb-2 flex flex-wrap gap-1.5" data-testid="facility-ai-suggestions">
          {SUGGESTED_PROMPTS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => sendMessage(p)}
              disabled={sending}
              className="text-[10px] font-semibold rounded-full border px-2.5 py-1 hover:bg-[#F8FAFC] transition disabled:opacity-60"
              style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
            >
              {p}
            </button>
          ))}
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
          className="px-4 py-3 border-t flex items-center gap-2"
          style={{ borderColor: "#E2E8F0" }}
        >
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Ask anything about this facility…"
            className="h-10 text-sm"
            disabled={sending}
            data-testid="facility-ai-input"
          />
          <Button
            type="submit"
            size="sm"
            disabled={sending || !draft.trim()}
            className="h-10 px-3"
            style={{ background: "#0B2545", color: "#fff" }}
            data-testid="facility-ai-send"
          >
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          </Button>
        </form>
      </section>
    </div>
  );
}

const SUGGESTED_PROMPTS = [
  "Draft a follow-up email",
  "Estimate pricing for 40 draws/month",
  "What should I do next?",
  "Recommend a contract length",
];

const seedConversation = (fac) => [
  {
    role: "assistant",
    text: `Hi — I'm scoped to ${fac?.facility_name || "this facility"}. I can draft outreach, estimate pricing, suggest follow-ups, or analyze the opportunity. What would you like to do?`,
  },
];

const BriefCard = ({ icon: Icon, label, value, accent, wrap = false, testId }) => (
  <div className="rounded-xl border bg-white px-4 py-3" style={{ borderColor: "#E2E8F0" }} data-testid={testId}>
    <div className="flex items-center gap-1.5">
      <Icon className="h-3 w-3" style={{ color: accent }} />
      <span className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#475569" }}>{label}</span>
    </div>
    <div className={`mt-1 font-display text-${wrap ? "sm" : "lg"} font-semibold leading-tight`}
      style={{ color: "#0B2545", wordBreak: "break-word" }}>
      {value}
    </div>
  </div>
);

const MessageBubble = ({ role, text, loading, error }) => {
  const isUser = role === "user";
  return (
    <div className={`flex gap-2 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser ? (
        <div className="grid place-items-center h-6 w-6 rounded-full shrink-0"
          style={{ background: "#0B2545", color: "#D4AF37" }}>
          <Bot className="h-3 w-3" />
        </div>
      ) : null}
      <div
        className="max-w-[78%] rounded-2xl px-3 py-2 text-[13px] leading-snug whitespace-pre-wrap"
        style={{
          background: isUser ? "#0B2545" : (error ? "rgba(220,38,38,0.06)" : "#F8FAFC"),
          color:      isUser ? "#FFFFFF" : (error ? "#991B1B" : "#0B2545"),
          border: isUser ? "none" : `1px solid ${error ? "rgba(220,38,38,0.25)" : "#E2E8F0"}`,
        }}
      >
        {loading ? (
          <span className="inline-flex items-center gap-1.5 text-[12px]">
            <Loader2 className="h-3 w-3 animate-spin" />
            Thinking…
          </span>
        ) : (
          text
        )}
      </div>
      {isUser ? (
        <div className="grid place-items-center h-6 w-6 rounded-full shrink-0"
          style={{ background: "#D4AF37", color: "#0B2545" }}>
          <UserIcon className="h-3 w-3" />
        </div>
      ) : null}
    </div>
  );
};
